import { FC } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCwIcon, AlertTriangleIcon } from "lucide-react";
import { useAuiState } from "@assistant-ui/react";
import { useReconnectStateStore } from "@/components/custom/stores/reconnectStateStore";

export const ReconnectingIndicator: FC = () => {
  const { t } = useTranslation();
  const remoteId = useAuiState(s => s.threadListItem.remoteId);
  const status = useReconnectStateStore(s => s.status);
  const attempt = useReconnectStateStore(s => s.attempt);
  const threadId = useReconnectStateStore(s => s.threadId);

  if (!remoteId || threadId !== remoteId) return null;

  if (status === "exhausted") {
    return (
      <div className="aui-reconnecting-banner pointer-events-none mx-auto mt-2 flex w-fit max-w-(--thread-max-width) items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-red-900 text-xs shadow-sm dark:border-red-700/40 dark:bg-red-900/20 dark:text-red-200">
        <AlertTriangleIcon className="size-3.5" />
        <span>{t("error.reconnectExhausted")}</span>
      </div>
    );
  }

  if (status === "connecting") {
    return (
      <div className="aui-reconnecting-banner pointer-events-none mx-auto mt-2 flex w-fit max-w-(--thread-max-width) items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-amber-900 text-xs shadow-sm dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
        <RefreshCwIcon className="size-3.5 animate-spin" />
        <span>{t("error.reconnecting")}</span>
      </div>
    );
  }

  // Hidden during the very first attempt — only show once we're actually
  // retrying (attempt counter is the *upcoming* attempt). Avoids flickering
  // the banner on transient wifi flaps that recover on the first retry.
  if (status !== "retrying" || attempt < 2) return null;

  return (
    <div className="aui-reconnecting-banner pointer-events-none mx-auto mt-2 flex w-fit max-w-(--thread-max-width) items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-amber-900 text-xs shadow-sm dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
      <RefreshCwIcon className="size-3.5 animate-spin" />
      <span>{t("error.reconnecting")}</span>
    </div>
  );
};
